"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Clock, Flag, MapPin } from "lucide-react";
import { EVENT, KV } from "@/components/home/data";

interface AuthShellProps {
  children: React.ReactNode;
  /** Content for the blue brand panel. Defaults to the race date block. */
  aside?: React.ReactNode;
  /** Right side of the header, next to the logo. */
  headerAction?: React.ReactNode;
  /** Wider form column for long forms. */
  wide?: boolean;
}

export const AuthAsideDate: React.FC = () => (
  <div className="py-16 lg:py-0">
    <p className="mes-label opacity-80">Salida</p>
    <p className="mes-display text-[clamp(3rem,9vw,8rem)] leading-[0.9] mt-2">
      14 Feb
      <br />
      2027
    </p>
    <div className="mes-label mt-5 flex flex-wrap items-center gap-x-5 gap-y-2">
      <span className="inline-flex items-center gap-1.5">
        <MapPin className="w-3.5 h-3.5 opacity-70" strokeWidth={2.25} aria-hidden />
        {EVENT.place}, {EVENT.country}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5 opacity-70" strokeWidth={2.25} aria-hidden />
        {EVENT.startTime}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Flag className="w-3.5 h-3.5 opacity-70" strokeWidth={2.25} aria-hidden />
        10KM, 21KM y 42KM
      </span>
    </div>
  </div>
);

/** Two-panel layout shared by login and register: cream form column + blue brand panel. */
const AuthShell: React.FC<AuthShellProps> = ({ children, aside, headerAction, wide }) => (
  <div className="min-h-screen grid grid-cols-1 lg:grid-cols-12 bg-brand-cream">
    <section
      className={
        wide
          ? "lg:col-span-7 xl:col-span-7 flex flex-col px-5 sm:px-10 lg:px-14 py-6 lg:py-8"
          : "lg:col-span-6 xl:col-span-5 flex flex-col px-5 sm:px-10 lg:px-14 py-6 lg:py-8"
      }
    >
      <header className="flex items-center justify-between gap-4">
        <Link href="/" className="block bg-brand-blue px-3 py-2" aria-label={EVENT.name}>
          <Image src={KV.lettering} alt={EVENT.name} width={2400} height={548} priority sizes="144px" className="w-32 md:w-36 h-auto" />
        </Link>
        {headerAction ?? (
          <Link href="/" className="mes-label text-brand-ink hover:text-brand-blue transition-colors">
            ← Inicio
          </Link>
        )}
      </header>

      <div className="flex-1 flex items-center py-12 lg:py-10">
        <div className={wide ? "w-full max-w-xl" : "w-full max-w-md"}>{children}</div>
      </div>

      <footer className="mes-label text-brand-ink/40">
        © {new Date().getFullYear()} {EVENT.name}
      </footer>
    </section>

    <aside
      className={
        (wide ? "lg:col-span-5 xl:col-span-5" : "lg:col-span-6 xl:col-span-7") +
        " relative bg-brand-blue text-white overflow-hidden min-h-[420px] lg:min-h-0 flex flex-col lg:sticky lg:top-0 lg:h-screen"
      }
    >
      <svg aria-hidden className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
        <g stroke="rgba(255,255,255,0.22)" strokeWidth="1">
          <line x1="0" y1="62" x2="100" y2="14" vectorEffect="non-scaling-stroke" />
          <line x1="0" y1="98" x2="100" y2="50" vectorEffect="non-scaling-stroke" />
          <line x1="22" y1="0" x2="58" y2="100" vectorEffect="non-scaling-stroke" />
          <line x1="52" y1="0" x2="88" y2="100" vectorEffect="non-scaling-stroke" />
        </g>
      </svg>
      <div className="absolute top-12 lg:top-16 -right-10 w-[80%] opacity-90 pointer-events-none">
        <Image src={KV.sunWaves} alt="" width={2400} height={1080} sizes="60vw" className="w-full h-auto" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col justify-between p-6 sm:p-10 lg:p-14">
        <div />

        {aside ?? <AuthAsideDate />}

        <div className="flex justify-end">
          <div className="w-28 md:w-36 shrink-0">
            <Image src={KV.sunburst} alt="" width={1600} height={813} sizes="144px" className="w-full h-auto" />
            <div className="h-[2px] bg-white" />
          </div>
        </div>
      </div>
    </aside>
  </div>
);

export default AuthShell;
